import { type RefObject, useCallback, useEffect, useRef } from 'react';

type ScrollAdapter = {
  scrollToOffset: (
    offset: number,
    options?: { align?: 'start' | 'center' | 'end' | 'auto' }
  ) => void;
};

type Options = {
  /**
   * Per-view key. Each key keeps its own saved position so toggling between
   * views (e.g. Regular vs Active Deal) restores the right one.
   */
  viewKey?: string;
  /**
   * Pass a `@tanstack/react-virtual` virtualizer for variable-height lists.
   * Snap-to-top routes through `scrollToOffset(0)` so the virtualizer's
   * cached offset stays in sync with the DOM `scrollTop = 0` write.
   */
  virtualizer?: ScrollAdapter;
  /**
   * Optional external store for saved positions. Provide a shared ref when the
   * scrolled component may unmount (e.g. when toggling between two different
   * components) so the saved positions outlive the component.
   */
  store?: RefObject<Record<string, number>>;
};

const RESTORE_QUIET_MS = 250;

/**
 * Preserve scroll position across a search interaction and across view toggles.
 *
 * - While `isSearching` is true, the scroll snaps to the top and scroll events
 *   aren't tracked.
 * - When `isSearching` flips back to false, the saved `scrollTop` for `viewKey`
 *   is restored.
 * - When `viewKey` changes, the saved position for the new key is restored.
 *
 * Programmatic scrolls (snap and restore) emit scroll events that would
 * otherwise overwrite the saved position with intermediate values; saves are
 * suppressed for `RESTORE_QUIET_MS` after each programmatic scroll.
 *
 * For variable-height virtualizers, the consumer must also override
 * `shouldAdjustScrollPositionOnItemSizeChange` to skip adjustments while the
 * DOM `scrollTop` and the virtualizer's cached `scrollOffset` disagree —
 * otherwise ResizeObserver-driven remeasurements race the restore write and
 * the virtualizer adjusts against a stale offset.
 */
export function useScrollPreservation(
  scrollRef: RefObject<HTMLElement | null>,
  isSearching: boolean,
  options: Options = {}
) {
  const viewKey = options.viewKey ?? 'default';
  const { virtualizer, store } = options;
  const internalStoreRef = useRef<Record<string, number>>({});
  const savedRef = store ?? internalStoreRef;
  const restoreUntilRef = useRef(0);

  const onScroll = useCallback(() => {
    if (isSearching || !scrollRef.current) {
      return;
    }

    if (performance.now() < restoreUntilRef.current) {
      return;
    }

    savedRef.current[viewKey] = scrollRef.current.scrollTop;
  }, [isSearching, scrollRef, viewKey, savedRef]);

  useEffect(() => {
    const element = scrollRef.current;
    if (!element) {
      return;
    }

    restoreUntilRef.current = performance.now() + RESTORE_QUIET_MS;

    if (isSearching) {
      if (virtualizer) {
        virtualizer.scrollToOffset(0);
      }

      element.scrollTop = 0;

      return;
    }

    const saved = savedRef.current[viewKey];
    if (saved === undefined) {
      return;
    }

    if (virtualizer) {
      virtualizer.scrollToOffset(saved);
    }

    element.scrollTop = saved;
  }, [isSearching, scrollRef, viewKey, virtualizer, savedRef]);

  return { onScroll };
}
